/*
* Device Management Page (Plese do not remove this comment)
* This page is used to manage the devices connected to the system.
* It provide the user-friendly interface to view the list of devices.
* The page should also include a search bar to filter the list of devices by clientId, or clientName.
* It use /api/v1/device/list to get the list of devices.
* The list of devices is displayed in a table.
* The table fields are : clientId, clientName, version, group, updatedAt, createdAt
* The group field is text input, managed by the admin. There are a apply button, normal hidden.
* After the admin change the group, the apply button will be shown.
* The admin can change the group of the device by clicking the edit button.
* The edit button will show a dialog to confirm the change.
* Clicking the apply button will send a post request to /api/v1/device/group/:clientId
* The request body should be in the format of { group: "new group" }
* The table is sorted by group, version, updatedAt.
*/
import React, { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { 
  Typography, Box, Container, Paper, TextField, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Button, IconButton, InputAdornment, CircularProgress, Snackbar, Alert, TablePagination
} from "@mui/material";
import { 
  Search as SearchIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Delete as DeleteIcon  // 新添加
} from "@mui/icons-material";
import { useAuthContext } from "../hooks/useAuthContext";

const DeviceManagement = () => {
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const { user } = useAuthContext();

  // 添加缺少的排序狀態
  const [orderBy, setOrderBy] = useState("group"); // 默認按 group 排序
  const [order, setOrder] = useState("asc"); // 默認升序排序

  // 添加缺少的分頁狀態
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25); // 默認每頁顯示25條記錄

  // For editing group
  const [editedGroups, setEditedGroups] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentClientId, setCurrentClientId] = useState(null);
  const [newGroup, setNewGroup] = useState("");

  // For deleting device (新添加)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);

  // Fetch devices on component mount
  useEffect(() => {
    fetchDevices();
  }, [user]);

  // Fetch devices from API
  const fetchDevices = async () => {
    try {
      setLoading(true);
      const response = await fetch("/api/v1/device/list", {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Fetched devices:", data);
      
      // Sort the devices initially
      const sortedDevices = sortDevices(data, orderBy, order);
      setDevices(sortedDevices);
      setFilteredDevices(sortedDevices);
      setLoading(false);
    } catch (err) {
      setError(`Failed to load devices: ${err.message}`);
      setLoading(false);
    }
  };

  // Filter devices based on search query
  useEffect(() => {
    const filtered = devices.filter(
      (device) =>
        device.clientId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        device.clientName?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredDevices(filtered);
  }, [searchQuery, devices]);

  // Sort devices
  const sortDevices = (devicesToSort, property, sortOrder) => {
    return [...devicesToSort].sort((a, b) => {
      const valueA = (a[property] || "").toString().toLowerCase();
      const valueB = (b[property] || "").toString().toLowerCase();
      
      if (sortOrder === "asc") {
        return valueA.localeCompare(valueB);
      } else {
        return valueB.localeCompare(valueA);
      }
    });
  };

  // Handle sort request
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setOrder(newOrder);
    setOrderBy(property);
    setFilteredDevices(sortDevices(filteredDevices, property, newOrder));
  };

  // Handle group change
  const handleGroupChange = (clientId, value) => {
    setEditedGroups({
      ...editedGroups,
      [clientId]: value,
    });
  };

  // Open edit dialog
  const handleEditClick = (clientId, currentGroup) => {
    setCurrentClientId(clientId);
    setNewGroup(currentGroup || "");
    setDialogOpen(true);
  };

  // Close dialog
  const handleDialogClose = () => {
    setDialogOpen(false);
    setCurrentClientId(null);
  };

  // Apply group change
  const handleGroupApply = async (clientId) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/device/group/${clientId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify({ group: editedGroups[clientId] }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Update the device in the list
      const updatedDevices = devices.map(device => 
        device.clientId === clientId 
          ? { ...device, group: editedGroups[clientId] } 
          : device
      );
      
      setDevices(updatedDevices);
      setFilteredDevices(sortDevices(updatedDevices, orderBy, order));
      
      // Clear the edited group
      const newEditedGroups = { ...editedGroups };
      delete newEditedGroups[clientId];
      setEditedGroups(newEditedGroups);
      
      setSuccess(`Group updated for device ${clientId}`);
    } catch (err) {
      setError(`Failed to update group: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Confirm group change from dialog
  const handleConfirmGroupChange = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/device/group/${currentClientId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify({ group: newGroup }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Update the device in the list
      const updatedDevices = devices.map(device => 
        device.clientId === currentClientId 
          ? { ...device, group: newGroup } 
          : device
      );
      
      setDevices(updatedDevices);
      setFilteredDevices(sortDevices(updatedDevices, orderBy, order));
      
      setSuccess(`Group updated for device ${currentClientId}`);
      handleDialogClose();
    } catch (err) {
      setError(`Failed to update group: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Open delete dialog (新添加)
  const handleDeleteClick = (device) => {
    setDeviceToDelete(device);
    setDeleteDialogOpen(true);
  };

  // Close delete dialog (新添加)
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setDeviceToDelete(null);
  };

  // Confirm device deletion (新添加)
  const handleConfirmDelete = async () => {
    if (!deviceToDelete) return;
    
    try {
      setLoading(true);
      const response = await fetch(`/api/v1/device/${deviceToDelete.clientId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user?.token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Remove the deleted device from the list
      const updatedDevices = devices.filter(
        device => device.clientId !== deviceToDelete.clientId
      );
      
      setDevices(updatedDevices);
      setFilteredDevices(
        updatedDevices.filter(
          device => 
            device.clientId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            device.clientName?.toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
      
      setSuccess(`Device ${deviceToDelete.clientId} successfully deleted`);
      setDeleteDialogOpen(false);
      setDeviceToDelete(null);
    } catch (err) {
      setError(`Failed to delete device: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Format date for display in YYYY-MM-DD HH:MM:SS format
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    
    try {
      const date = new Date(dateString);
      
      // 檢查日期是否有效
      if (isNaN(date.getTime())) return "Invalid Date";
      
      // Add leading zero if needed
      const pad = (num) => num.toString().padStart(2, '0');
      
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1); // Months are 0-indexed
      const day = pad(date.getDate());
      const hours = pad(date.getHours());
      const minutes = pad(date.getMinutes());
      const seconds = pad(date.getSeconds());
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (err) {
      console.error("Date formatting error:", err);
      return "Error";
    }
  };

  // Clear notifications
  const handleCloseAlert = () => {
    setSuccess(null);
    setError(null);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ mb: 5 }}>
      <Box sx={{ my: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Device Management
        </Typography>
      </Box>
      
      {/* Error and Success Alerts */}
      <Snackbar 
        open={Boolean(success)} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
      
      <Snackbar 
        open={Boolean(error)} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      {/* User List Paper - 與 UserManagement 風格一致 */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Device List</Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Search Devices"
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={fetchDevices}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>
        
        {loading && devices.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === "clientId"}
                        direction={orderBy === "clientId" ? order : "asc"}
                        onClick={() => handleRequestSort("clientId")}
                      >
                        Client ID
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === "clientName"}
                        direction={orderBy === "clientName" ? order : "asc"}
                        onClick={() => handleRequestSort("clientName")}
                      >
                        Client Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === "version"}
                        direction={orderBy === "version" ? order : "asc"}
                        onClick={() => handleRequestSort("version")}
                      >
                        Version
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === "group"}
                        direction={orderBy === "group" ? order : "asc"}
                        onClick={() => handleRequestSort("group")}
                      >
                        Group
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Actions</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === "updatedAt"}
                        direction={orderBy === "updatedAt" ? order : "asc"}
                        onClick={() => handleRequestSort("updatedAt")}
                      >
                        Updated At
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === "createdAt"}
                        direction={orderBy === "createdAt" ? order : "asc"}
                        onClick={() => handleRequestSort("createdAt")}
                      >
                        Created At
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDevices.length > 0 ? (
                    filteredDevices
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((device) => (
                        <TableRow key={device.clientId} hover>
                          <TableCell>{device.clientId}</TableCell>
                          <TableCell>{device.clientName || "N/A"}</TableCell>
                          <TableCell>{device.version || "N/A"}</TableCell>
                          <TableCell>{device.group || "N/A"}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleEditClick(device.clientId, device.group || "")}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleDeleteClick(device)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                          <TableCell>{formatDate(device.updatedAt)}</TableCell>
                          <TableCell>{formatDate(device.createdAt)}</TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        {loading ? "Loading..." : "No devices found"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredDevices.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ width: '100%' }}
            />
          </>
        )}
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Edit Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Update the group for device {currentClientId}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Group"
            type="text"
            fullWidth
            variant="outlined"
            value={newGroup}
            onChange={(e) => setNewGroup(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleConfirmGroupChange} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete device with ID: <strong>{deviceToDelete?.clientId}</strong>?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Outlet />
    </Container>
  );
};

export default DeviceManagement;
